import { useState, useEffect, useCallback } from 'react';
import { getCourseContent } from '../../../utils/progressUtils';
import {
  resetToActualProgress,
  getDisplayedContent,
  getDisplayedContentType,
  getDisplayedContentIndex,
  handleBackNavigation,
  handleForwardBrowsing
} from '../utils/contentNavigation';
import { logModalStateChange, getStoredCompletion, setStoredCompletion } from '../utils/modalHelpers';

/**
 * Custom hook for managing modal navigation state and behavior
 * @param {Object} user - Current user object
 * @param {Object} userCurrentCourse - Currently active course
 * @param {Object} userCurrentContent - Currently active content
 * @param {string} currentContentType - Type of current content
 * @param {Object} navigationContext - Navigation context from Redux
 * @param {Array} pathCourses - Array of courses in the learning path
 * @param {Object} userProgress - User's progress data
 * @param {string} lastAction - Last action performed (from Redux)
 * @param {Function} isPathCompletedLocal - Function to check if path is completed
 * @returns {Object} Modal navigation state and handlers
 */
export const useModalNavigation = (
  user,
  userCurrentCourse,
  userCurrentContent,
  currentContentType,
  navigationContext,
  pathCourses,
  userProgress,
  lastAction,
  isPathCompletedLocal
) => {
  // Modal state
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false);

  // Initialize completion modal state from localStorage
  const [hasShownCompletionModal, setHasShownCompletionModal] = useState(() => {
    const userId = user?.id;
    const pathId = user?.path?._id;

    if (userId && pathId) {
      const stored = getStoredCompletion(userId, pathId);
      if (stored) return true;
    }

    // Fallback to progress data (may be undefined on initial load)
    return userProgress?.isPathCompleted || false;
  });

  // Modal navigation state for cross-course back navigation
  const [modalNavigation, setModalNavigation] = useState({
    viewingContent: null,          // Currently viewing content (can go backwards)
    viewingContentType: null,      // Type of currently viewing content
    viewingIndex: -1,              // Index of currently viewing content (local to course)
    globalIndex: -1,               // Global index across all courses
    viewingCourse: null,           // Course containing viewed content
    isViewingBackwards: false,     // Flag to track if viewing previous content
    allPathContent: [],            // All content across all courses
    canNavigateBack: false,        // Computed from completion status
    canNavigateForward: false      // Computed from viewing state
  });

  // Debug modal state changes
  const setShowContentModalWithDebug = useCallback((value) => {
    logModalStateChange(value, {
      previousState: showLessonModal,
      currentCourse: userCurrentCourse,
      currentContent: userCurrentContent,
      currentContentType: currentContentType
    });
    setShowLessonModal(value);
  }, [showLessonModal, userCurrentCourse, userCurrentContent, currentContentType]);

  // Get content for the current course in modal (excluding reinforcements)
  const modalCourseContent = userCurrentCourse ?
    getCourseContent(userCurrentCourse).filter(content => {
      const isReinforcement = userCurrentCourse.reinforcements?.some(r => r._id === content._id);
      return !isReinforcement; // Only keep lessons and submission forms
    }) : [];

  // Get current content index for modal (based on filtered content without reinforcements)
  const currentContentIndex = userCurrentCourse ? (() => {
    const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;
    const index = modalCourseContent.findIndex(content => content._id === contentId);
    return index;
  })() : -1;

  // Navigation helper functions
  const resetToActualProgressLocal = useCallback(() => {
    resetToActualProgress(setModalNavigation, navigationContext);
  }, [navigationContext]);

  const getDisplayedContentLocal = useCallback(() => {
    return getDisplayedContent(modalNavigation, userCurrentContent);
  }, [modalNavigation, userCurrentContent]);

  const getDisplayedContentTypeLocal = useCallback(() => {
    return getDisplayedContentType(modalNavigation, currentContentType);
  }, [modalNavigation, currentContentType]);

  const getDisplayedContentIndexLocal = useCallback(() => {
    return getDisplayedContentIndex(modalNavigation, currentContentIndex);
  }, [modalNavigation, currentContentIndex]);

  // Navigation handlers
  const handleBackNavigationLocal = useCallback(() => {
    handleBackNavigation(modalNavigation, navigationContext, pathCourses, userProgress, setModalNavigation);
  }, [modalNavigation, navigationContext, pathCourses, userProgress]);

  const handleForwardBrowsingLocal = useCallback(() => {
    handleForwardBrowsing(modalNavigation, navigationContext, pathCourses, userProgress, setModalNavigation, resetToActualProgressLocal);
  }, [modalNavigation, navigationContext, pathCourses, userProgress, resetToActualProgressLocal]);

  const handleCloseLessonModal = useCallback(() => {
    setShowContentModalWithDebug(false);
    // Note: Achievement view close is handled by achievement hook
  }, [setShowContentModalWithDebug]);

  // Auto-open content modal only after explicit course start action
  useEffect(() => {
    // Only auto-open modal after a START_COURSE action, not for existing progress
    if (userCurrentCourse && userCurrentContent && !showLessonModal && !hasAutoOpenedModal &&
        lastAction === 'START_COURSE') {
      setShowContentModalWithDebug(true);
      setHasAutoOpenedModal(true);
    }

    // Ensure hasAutoOpenedModal is set to true if modal is already open
    if (showLessonModal && !hasAutoOpenedModal) {
      setHasAutoOpenedModal(true);
    }
  }, [userCurrentCourse, userCurrentContent, showLessonModal, hasAutoOpenedModal, lastAction, setShowContentModalWithDebug]);

  // Track when completion modal is shown to prevent auto-opening on subsequent visits
  useEffect(() => {
    if (showLessonModal && isPathCompletedLocal() && !hasShownCompletionModal) {
      // Store in localStorage for persistence
      const userId = user?.id;
      const pathId = user?.path?._id;
      setStoredCompletion(userId, pathId, true);
      setHasShownCompletionModal(true);
    }
  }, [showLessonModal, isPathCompletedLocal, hasShownCompletionModal, user?.id, user?.path?._id]);

  // Manage body scroll when modal is open
  useEffect(() => {
    if (showLessonModal) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [showLessonModal]);

  // Manage body scroll when course detail modal is open
  useEffect(() => {
    if (selectedCourse) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [selectedCourse]);

  return {
    // Modal state
    showLessonModal,
    setShowLessonModal,
    selectedCourse,
    setSelectedCourse,
    hasAutoOpenedModal,
    setHasAutoOpenedModal,
    hasShownCompletionModal,
    setHasShownCompletionModal,
    modalNavigation,
    setModalNavigation,

    // Modal content
    currentContentIndex,
    modalCourseContent,

    // Helper functions
    setShowContentModalWithDebug,
    resetToActualProgressLocal,
    getDisplayedContentLocal,
    getDisplayedContentTypeLocal,
    getDisplayedContentIndexLocal,

    // Navigation handlers
    handleBackNavigationLocal,
    handleForwardBrowsingLocal,
    handleCloseLessonModal
  };
};